import { useState } from 'react';
import {
  Modal,
  PrimaryButton,
  SecondaryButton,
  Typography,
  useSnackbar,
  SnackbarStatus,
} from '@weareopenr/component-library';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import PriceSchemeSelectorBase from '../PriceSchemeSelector/PriceSchemeSelectorBase';
import { useValidate } from 'src/hooks/useValidate/useValidate';
import { getValidationRules } from 'src/utils/schedules/getValidationRules';
import { EVENT_TYPES } from 'src/api/getEvents/types';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import ModalImg from 'src/assets/images/chef-plating.png';
import styles from './MenuValidationModal.module.scss';

interface IMenuValidationModal {
  isOpen: boolean;
  closeModal: () => void;
  menuId: string;
  menuName: string;
}

const MenuValidationModal = ({ isOpen, closeModal, menuId, menuName }: IMenuValidationModal) => {
  const [priceSchemeId, setPriceSchemeId] = useState<string | undefined>(undefined);
  const { showSnackbar } = useSnackbar();
  const { captureException } = useUserTracking();
  const validate = useValidate();

  const handleClose = () => {
    setPriceSchemeId(undefined);
    closeModal();
  };

  const handleValidate = async () => {
    if (!priceSchemeId) return;

    try {
      await validate.mutateAsync({
        criteria: {
          menu_id: menuId,
          price_scheme_id: priceSchemeId,
          rules: getValidationRules(EVENT_TYPES.PUBLISH_MENU),
        },
      });
      showSnackbar({ status: SnackbarStatus.PROGRESS, message: 'Your menu validation is in progress' });
      handleClose();
    } catch (err) {
      captureException(err, { tags: { action: 'validate_menu', menuId } });
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'Failed to validate menu. Please try again.' });
    }
  };

  return (
    <Modal open={isOpen} closeModal={handleClose} image={ModalImg} imageAlt="Picture of a chef plating up">
      <>
        <div className={styles['header']}>
          <Typography weight="bold" size="medium" className={styles['eyebrow']}>
            Menu Validation
          </Typography>
          <Typography size="xxlarge">
            Validate <strong>{menuName}</strong>
          </Typography>
          <Typography size="medium">
            Choose a price scheme to validate for missing prices, POS ID, Tax Rate, and more.
          </Typography>
        </div>
        <div className={styles['section']}>
          <Typography weight="bold" size="medium">
            Pricing
          </Typography>
          <PriceSchemeSelectorBase value={priceSchemeId} onChange={setPriceSchemeId} />
        </div>
        <ButtonContainer
          left={
            <SecondaryButton
              size="full-width"
              buttonStyle="outline"
              onClick={handleClose}
              disabled={validate.isLoading}
            >
              Cancel
            </SecondaryButton>
          }
          right={
            <PrimaryButton
              size="full-width"
              onClick={handleValidate}
              disabled={!priceSchemeId || validate.isLoading}
              loading={validate.isLoading}
            >
              Run Validation
            </PrimaryButton>
          }
        />
      </>
    </Modal>
  );
};

export default MenuValidationModal;
